import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import "../css/SupplierPaymentHistory.css";
// For modal styling (simple inline for now)

const formatRupee = (amount) => `₹${Number(amount).toFixed(2)}`;

const SupplierPaymentHistory = () => {
  const { id } = useParams();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const LIMIT = 12;

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}get-payment-list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        });
        const data = await response.json();
        if (data.status && Array.isArray(data.invoice_data)) {
          setInvoices(data.invoice_data);
          setTotalPages(Math.ceil(data.invoice_data.length / LIMIT));
        } else {
          setInvoices([]);
          setTotalPages(1);
        }
      } catch (error) {
        setInvoices([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchPayments();
  }, [id]);

  // Pagination logic: 12 per page
  const paginatedInvoices = invoices.slice((page - 1) * LIMIT, page * LIMIT);

  // Calculate total due amount for the current page
  const totalDueAmount = paginatedInvoices.reduce((sum, p) => sum + (Number(p.due_amount) || 0), 0);
  // Calculate total amount for the current page
  const totalAmount = paginatedInvoices.reduce(
    (sum, p) => sum + (Number(p.total_amount) || 0),
    0
  );
  // Calculate total paid amount for the current page
  const totalPaidAmount = paginatedInvoices.reduce(
    (sum, p) => sum + (Number(p.total_paid) || 0),
    0
  );

  return (
    <div className="category-list-content modern-supplier-history">
      <div className="page-header modern-header">
        <div className="header-content">
          <h1>Supplier Payment History</h1>
          <p>View all payments made to this supplier</p>
        </div>
      </div>
      {/* Remove supplier name display here */}
      <div className="unit-list-table-container modern-table-container">
        <table className="unit-list-table modern-table">
          <thead className="modern-thead">
            <tr>
              <th>Invoice No</th>
              <th>Supplier Name</th>
              <th>Total Amount</th>
              <th>Paid Amount</th>
              <th>Due Amount</th>
              <th>Payment Date</th>
              <th>Payment Method</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10">Loading...</td></tr>
            ) : paginatedInvoices.length > 0 ? (
              paginatedInvoices.map((invoice) => (
                <tr key={invoice.invoice_no} className="modern-row">
                  <td>{invoice.invoice_no}</td>
                  <td>{invoice.supplier_name}</td>
                  <td>{formatRupee(invoice.total_amount)}</td>
                  <td>{formatRupee(invoice.total_paid)}</td>
                  <td>{formatRupee(invoice.due_amount)}</td>
                  <td>{invoice.payment_date ? invoice.payment_date.split(' ')[0] : ''}</td>
                  <td>{invoice.payment_method || "-"}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      title="View History"
                      className="modern-action-icon"
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setModalOpen(true);
                      }}
                    >🕑</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10">No payment records found for this supplier.</td>
              </tr>
            )}
          </tbody>
          {/* Summary Row for Due Amount and Total Amount */}
          {paginatedInvoices.length > 0 && (
            <tfoot>
              <tr className="modern-summary-row">
                <td colSpan="4" className="modern-summary-cell">Total Amount: {formatRupee(totalAmount)}</td>
                <td colSpan="3" className="modern-summary-cell">Total Paid Amount: {formatRupee(totalPaidAmount)}</td>
                <td colSpan="3" className="modern-summary-cell">
                  {totalDueAmount === 0
                    ? <>Status: <span className="payment-completed-badge">Payment Completed</span></>
                    : <>Due Amount: {formatRupee(totalDueAmount)}</>}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="table-footer modern-table-footer">
        <div className="results-info modern-results-info">
          Page {page} of {totalPages}
        </div>
        <div className="pagination modern-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="modern-pagination-btn"
          >
            Previous
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="modern-pagination-btn"
          >
            Next
          </button>
        </div>
      </div>
      {/* Modal for payment history by invoice */}
      {modalOpen && selectedInvoice && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <div className="modern-modal-header">
              <div>
                <div className="modern-modal-supplier">Supplier: {selectedInvoice.supplier_name}</div>
                <h2 className="modern-modal-invoice">Invoice: {selectedInvoice.invoice_no}</h2>
              </div>
              <button onClick={() => setModalOpen(false)} className="modern-modal-close">&times;</button>
            </div>
            <div className="modern-modal-body">
              <table className="modern-modal-table">
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>Date</th>
                    <th>Product Name</th>
                    <th>Per Unit Price</th>
                    <th>Quantity</th>
                    <th>Total Amount</th>
                    <th>Paid Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.history_data && selectedInvoice.history_data.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.purchase_date}</td>
                      <td>{item.product_name}</td>
                      <td>{formatRupee(item.per_unit_price)}</td>
                      <td>{item.quantity}</td>
                      <td>{formatRupee(item.total_amount)}</td>
                      <td>{formatRupee(item.paid_amount)}</td>
                    </tr>
                  ))}
                  {selectedInvoice.history_data && selectedInvoice.history_data.length > 0 && (
                    <tr className="modern-modal-summary-row">
                      <td colSpan={5}>Total</td>
                      <td>{formatRupee(selectedInvoice.total_amount)}</td>
                      <td>{formatRupee(selectedInvoice.total_paid)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Due Amount shown after last row, in red */}
              <div className="modern-modal-due">
                Due Amount: {formatRupee(selectedInvoice.due_amount)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPaymentHistory; 